/**
 * API Helper Utilities
 * Centralized functions for making API calls to the backend
 */

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const CHUNK_SERVER_URL = import.meta.env.VITE_CHUNK_SERVER_URL;

function requireEnv(name: string, value?: string): string {
  if (!value) {
    throw new Error(`Missing required environment variable: ${name}`);
  }
  return value;
}

/**
 * Get the backend base URL
 */
export function getBackendUrl(): string {
  return requireEnv('VITE_BACKEND_URL', BACKEND_URL);
}

/**
 * Get the chunk server base URL
 */
export function getChunkServerUrl(): string {
  return requireEnv('VITE_CHUNK_SERVER_URL', CHUNK_SERVER_URL);
}

/**
 * Get the chunk server WebSocket URL
 */
export function getChunkServerWsUrl(): string {
  const base = getChunkServerUrl().replace(/\/$/, '');
  if (base.startsWith('ws://') || base.startsWith('wss://')) {
    return base;
  }
  if (base.startsWith('http://')) {
    return base.replace(/^http:\/\//, 'ws://');
  }
  if (base.startsWith('https://')) {
    return base.replace(/^https:\/\//, 'wss://');
  }
  return `wss://${base}`;
}

/**
 * Get authentication token from localStorage
 */
export function getAuthToken(): string | null {
  if (typeof window === 'undefined') return null;
  let token = localStorage.getItem('runara_auth_token');
  if (!token) {
    token = localStorage.getItem('obelisk_auth_token');
    if (token) {
      localStorage.setItem('runara_auth_token', token);
      localStorage.removeItem('obelisk_auth_token');
    }
  }
  return token;
}

/**
 * Get headers for authenticated requests
 */
export function getAuthHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
}

/**
 * Make a fetch request to the backend API
 */
export async function backendFetch(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${BACKEND_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  // Add auth token if available
  const token = getAuthToken();
  if (token && !('Authorization' in headers)) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  return fetch(url, {
    ...options,
    headers,
  });
}

/**
 * Handle authentication errors (401 Unauthorized)
 * Clears invalid token and redirects to login
 */
export async function handleAuthError(response: Response): Promise<boolean> {
  if (response.status === 401) {
    // Clear invalid token
    if (typeof window !== 'undefined') {
      localStorage.removeItem('runara_auth_token');
      // Redirect to login
      window.location.href = '/';
    }
    return true; // Indicates error was handled
  }
  return false;
}

/**
 * Wait for session to be ready after auth state change
 * Useful after signup/login to ensure session is refreshed
 */
export async function waitForSession(maxWaitMs: number = 2000): Promise<boolean> {
  const { AuthService } = await import('@/services/AuthService');
  const startTime = Date.now();
  
  while (Date.now() - startTime < maxWaitMs) {
    try {
      const session = await AuthService.getSession();
      if (session) {
        return true;
      }
    } catch {
      // Session not ready yet
    }
    // Wait 100ms before retrying
    await new Promise(resolve => setTimeout(resolve, 100));
  }
  
  return false;
}

/**
 * Helper to convert /api/... paths to backend API paths
 * Maps Next.js API routes to backend API routes
 */
export function getApiPath(apiPath: string): string {
  // If it's already a full URL, return as-is
  if (apiPath.startsWith('http')) {
    return apiPath;
  }
  
  // Remove leading /api if present (backend routes don't need it)
  const cleanPath = apiPath.startsWith('/api/') ? apiPath.slice(4) : apiPath;
  
  // Ensure leading slash
  return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
}
